import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated, getUserPrivileges, getUserRole } from '../../../utils/auth';
import Sidebar from '../../../components/Sidebar/sidebar';
import Header from '../../../components/Header';
import './RetailAddPolicy.css';
import api from '../../../utils/api'
import {
    Building2,
    ChevronDown,
    CircleDollarSign,
    ClipboardCheck,
    Download,
    FileText,
    RotateCcw,
    Save,
    Send,
    ShieldCheck,
    UploadCloud,
    UserRound
} from 'lucide-react';
import { API_BASE_URL } from '../../../utils/apiBase';
import { convertedPolicyAccessPrivileges } from '../../../utils/convertedPolicyPrivileges';
import { POLICY_TRANSACTION_PRIVILEGES } from '../../../utils/policyTransactionPrivileges';

const initialQuickBranchForm = {
    insurerId: '',
    insurerName: '',
    branchName: '',
    contactPersonName: '',
    contactNumber: '',
    email: '',
    address: '',
    pinCode: '',
    city: '',
    state: ''
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const pinCodeRegex = /^\d{6}$/;
const contactNumberRegex = /^[0-9+()\-\s]{6,15}$/;

const RetailAddPolicy = () => {
    const [collapsed, setCollapsed] = useState(false);
    const userPrivileges = getUserPrivileges();
    const isAdmin = getUserRole() === 'admin';
    const canAccessRetailPolicies =
        isAdmin || convertedPolicyAccessPrivileges('retail').some((key) => userPrivileges.includes(key));
    const canAccessCorporatePolicies =
        isAdmin || convertedPolicyAccessPrivileges('corporate').some((key) => userPrivileges.includes(key));
    const canBulkRetailPolicyUpload =
        isAdmin ||
        userPrivileges.includes(POLICY_TRANSACTION_PRIVILEGES.addPolicy.bulkRetailUpload) ||
        userPrivileges.includes(POLICY_TRANSACTION_PRIVILEGES.addPolicy.bulkUpload);
    const canBulkCorporatePolicyUpload =
        isAdmin ||
        userPrivileges.includes(POLICY_TRANSACTION_PRIVILEGES.addPolicy.bulkCorporateUpload) ||
        userPrivileges.includes(POLICY_TRANSACTION_PRIVILEGES.addPolicy.bulkUpload);
    const canBulkCombinedPolicyUpload = canBulkRetailPolicyUpload && canBulkCorporatePolicyUpload;
    const canBulkPolicyUpload = canBulkRetailPolicyUpload || canBulkCorporatePolicyUpload;
    const canDownloadBulkPolicyTemplate =
        isAdmin || userPrivileges.includes(POLICY_TRANSACTION_PRIVILEGES.addPolicy.bulkTemplateDownload);
    const defaultClientType = canAccessRetailPolicies
        ? 'Retail'
        : canAccessCorporatePolicies
            ? 'Corporate'
            : 'Retail';
    const today = new Date().toISOString().split("T")[0];
    const [formData, setFormData] = useState({
        clientType: defaultClientType,
        clientName: '',
        insurerName: '',
        branchName: '',
        transactionDate: today,
        transactionType: '',
        amountReceived: '',
        paymentMode: '',
        businessType: '',
        classOfPolicyId: '',
        classOfPolicyName: '',
        policyName: '',
        coverageType: '',
        numberOfLives: '',
        policyNumber: '',
        policyIssueDate: '',
        policyStartDate: '',
        policyEndDate: '',
        policyToBeRenewed: false,
        grossPremium: '',
        gstPercentage: '',
        gstAmount: '',
        netPremium: '',
        baseBrokeragePercentage: '',
        commissionAdjustmentType: 'add',
        commissionAdjustmentPercentage: '',
        brokeragePercentage: '',
        totalBrokerage: ''
    });

    const [policyDoc, setPolicyDoc] = useState(null);
    const [insurerInput, setInsurerInput] = useState('');
    const [insurerSuggestions, setInsurerSuggestions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [focused, setFocused] = useState(false);
    const [branches, setBranches] = useState([]);
    const [selectedInsurer, setSelectedInsurer] = useState(null);
    const [policyNames, setPolicyNames] = useState([]);
    const [classOfPolicyOptions, setClassOfPolicyOptions] = useState([]);

    const [clientInput, setClientInput] = useState('');
    const [clientSuggestions, setClientSuggestions] = useState([]);
    const [clientFocused, setClientFocused] = useState(false);
    const [clientSelectedIndex, setClientSelectedIndex] = useState(-1);

    const [policyError, setPolicyError] = useState('');
    const [policyExists, setPolicyExists] = useState(false);

    const policyDocRef = useRef(null);
    const [showCoverageType, setShowCoverageType] = useState(false);
    const [showLivesCovered, setShowLivesCovered] = useState(false);
    const [isConverted, setIsConverted] = useState(false);
    const [prospectId, setProspectId] = useState(null);
    const [showQuickBranchPopup, setShowQuickBranchPopup] = useState(false);
    const [quickBranchForm, setQuickBranchForm] = useState(initialQuickBranchForm);
    const [quickBranchErrors, setQuickBranchErrors] = useState({});
    const [quickBranchSaving, setQuickBranchSaving] = useState(false);
    const [quickBranchPinLookupLoading, setQuickBranchPinLookupLoading] = useState(false);
    const [quickBranchNotice, setQuickBranchNotice] = useState({ message: '', type: '' });
    const [currentDraftId, setCurrentDraftId] = useState(null);
    const [savingDraft, setSavingDraft] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [autoSaveStatus, setAutoSaveStatus] = useState('Ready');
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [openSections, setOpenSections] = useState({
        client: true,
        insurer: true,
        policy: true,
        premium: true,
        document: true
    });
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [bulkUploadFile, setBulkUploadFile] = useState(null);
    const [bulkUploadStep, setBulkUploadStep] = useState('select');
    const [bulkUploadClientType, setBulkUploadClientType] = useState('');
    const [bulkUploadSummary, setBulkUploadSummary] = useState(null);
    const [bulkUploadError, setBulkUploadError] = useState('');
    const [bulkUploadDragActive, setBulkUploadDragActive] = useState(false);
    const [bulkDocumentOpen, setBulkDocumentOpen] = useState(false);
    const [bulkDocumentFiles, setBulkDocumentFiles] = useState([]);
    const [bulkDocumentUploading, setBulkDocumentUploading] = useState(false);
    const [bulkDocumentResult, setBulkDocumentResult] = useState(null);
    const [bulkDocumentError, setBulkDocumentError] = useState('');
    const [bulkDocumentDragActive, setBulkDocumentDragActive] = useState(false);


    const location = useLocation();
    const navigate = useNavigate();
    const branchInfoMessages = [];
    const requiredFieldNames = [
        'clientName',
        'insurerName',
        'branchName',
        'transactionDate',
        'paymentMode',
        'businessType',
        'classOfPolicyId',
        'policyName',
        'policyNumber',
        'policyIssueDate',
        'policyStartDate',
        'policyEndDate',
        'grossPremium'
    ];
    const completedRequiredFields = requiredFieldNames.filter(name => {
        if (name === 'clientName') return String(clientInput || formData.clientName || '').trim();
        if (name === 'insurerName') return String(insurerInput || formData.insurerName || '').trim();
        return String(formData[name] || '').trim();
    }).length;
    const formCompletion = Math.round((completedRequiredFields / requiredFieldNames.length) * 100);
    const policySummary = [
        formData.clientName || clientInput,
        formData.insurerName || insurerInput,
        formData.policyName,
        formData.policyNumber
    ].filter(Boolean).join(' • ');
    const formatFileSize = bytes => {
        if (!bytes) return '0 KB';
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };
    const toggleSection = sectionKey => {
        setOpenSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    };

    if (selectedInsurer?._id && branches.length === 0) {
        branchInfoMessages.push({
            text: 'No approved branch found. Use Add Branch to submit a request.',
            type: 'info'
        });
    }

    const formatDraftDateForInput = value => {
        if (!value) return '';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return '';
        return parsed.toISOString().split('T')[0];
    };

    const applyPolicyToForm = useCallback(policy => {
        setFormData(prev => ({
            ...prev,
            clientType: policy.clientType || defaultClientType,
            clientName: policy.clientName || '',
            insurerName: policy.insurerName || '',
            branchName: policy.branchName || '',
            transactionDate: formatDraftDateForInput(policy.transactionDate) || today,
            transactionType: policy.transactionType || '',
            amountReceived: policy.amountReceived ?? '',
            paymentMode: policy.paymentMode || '',
            businessType: policy.businessType || '',
            classOfPolicyId: policy.classOfPolicyId || '',
            classOfPolicyName: policy.classOfPolicyName || '',
            policyName: policy.policyName || '',
            coverageType: policy.coverageType || '',
            numberOfLives: policy.numberOfLives || '',
            policyNumber: policy.policyNumber || '',
            policyIssueDate: formatDraftDateForInput(policy.policyIssueDate),
            policyStartDate: formatDraftDateForInput(policy.policyStartDate),
            policyEndDate: formatDraftDateForInput(policy.policyEndDate),
            policyToBeRenewed: Boolean(policy.policyToBeRenewed),
            grossPremium: policy.grossPremium ?? '',
            gstPercentage: policy.gstPercentage ?? '',
            gstAmount: policy.gstAmount ?? '',
            netPremium: policy.netPremium ?? '',
            baseBrokeragePercentage: policy.baseBrokeragePercentage ?? policy.brokeragePercentage ?? '',
            commissionAdjustmentType: policy.commissionAdjustmentType || 'add',
            commissionAdjustmentPercentage: policy.commissionAdjustmentPercentage ?? '',
            brokeragePercentage: policy.brokeragePercentage ?? '',
            totalBrokerage: policy.totalBrokerage ?? ''
        }));

        setClientInput(policy.clientName || '');
        setInsurerInput(policy.insurerName || '');
        setPolicyError('');
        setPolicyExists(false);
        setIsConverted(false);
        setProspectId(null);
    }, [defaultClientType, today]);

    if (quickBranchNotice.message) {
        branchInfoMessages.push({
            text: quickBranchNotice.message,
            type: quickBranchNotice.type || 'info'
        });
    }

    const applyClassSelection = classRow => {
        if (!classRow) return;

        const categoryName = String(classRow.categoryName || '').toLowerCase();

        setFormData(prev => ({
            ...prev,
            classOfPolicyId: classRow._id || '',
            classOfPolicyName: classRow.classOfPolicyName || ''
        }));

        if (categoryName === 'life') {
            setShowCoverageType(true);
        } else {
            setShowCoverageType(false);
            setShowLivesCovered(false);
            setFormData(prev => ({
                ...prev,
                coverageType: '',
                numberOfLives: ''
            }));
        }
    };


    // ================= CLIENT SEARCH (RETAIL / CORPORATE) =================
    useEffect(() => {
        const fetchClientSuggestions = async () => {
            if (!clientFocused || clientInput.length < 1) {
                setClientSuggestions([]);
                return;
            }

            try {
                const apiUrl =
                    formData.clientType === 'Retail'
                        ? `${API_BASE_URL}/api/add-policy/client-search?query=${clientInput}`
                        : `${API_BASE_URL}/api/add-policy/corporate-search?query=${clientInput}`;

                const res = await api.get(apiUrl);
                setClientSuggestions(res.data);
            } catch (err) {
                console.error('Failed to fetch client names', err);
            }
        };

        const debounce = setTimeout(fetchClientSuggestions, 300);
        return () => clearTimeout(debounce);
    }, [clientInput, clientFocused, formData.clientType]);

    // ================= INSURER SEARCH =================
    useEffect(() => {
        const fetchSuggestions = async () => {
            if (!focused || insurerInput.length < 1) {
                setInsurerSuggestions([]);
                return;
            }

            try {
                const res = await api.get(
                    `${API_BASE_URL}/api/add-policy/insurer-search?query=${insurerInput}`
                );
                setInsurerSuggestions(res.data);
            } catch (err) {
                console.error('Failed to fetch insurer names', err);
            }
        };

        const debounceTimer = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(debounceTimer);
    }, [insurerInput, focused]);

    // ================= LOAD POLICY NAMES BY CLASS OF POLICY =================
    useEffect(() => {
        const fetchPolicyNames = async () => {
            if (!formData.classOfPolicyId) {
                setPolicyNames([]);
                return;
            }

            try {
                const res = await api.get(
                    `${API_BASE_URL}/api/add-policy/policy-names?classOfPolicyId=${encodeURIComponent(formData.classOfPolicyId)}`
                );
                setPolicyNames(Array.isArray(res.data) ? res.data : []);
            } catch (error) {
                console.error('Error loading policy names:', error);
                setPolicyNames([]);
            }
        };

        fetchPolicyNames();
    }, [formData.classOfPolicyId]);

    useEffect(() => {
        const fetchClassOfPolicies = async () => {
            try {
                const res = await api.get(
                    `${API_BASE_URL}/api/add-policy/class-of-policies`
                );
                setClassOfPolicyOptions(Array.isArray(res.data) ? res.data : []);
            } catch (error) {
                console.error('Error loading class of policies:', error);
                setClassOfPolicyOptions([]);
            }
        };

        fetchClassOfPolicies();
    }, []);

    useEffect(() => {
        setAutoSaveStatus('Autosaving...');
        const draftTimer = setTimeout(() => {
            try {
                localStorage.setItem(
                    'addPolicyLocalDraft',
                    JSON.stringify({
                        formData,
                        clientInput,
                        insurerInput,
                        savedAt: new Date().toISOString()
                    })
                );
                setAutoSaveStatus('Autosaved');
            } catch (error) {
                setAutoSaveStatus('Autosave unavailable');
            }
        }, 900);

        return () => clearTimeout(draftTimer);
    }, [formData, clientInput, insurerInput]);

    useEffect(() => {
        if (!formData.classOfPolicyId || classOfPolicyOptions.length === 0) return;
        const selectedClass = classOfPolicyOptions.find(
            item => String(item._id) === String(formData.classOfPolicyId)
        );
        if (selectedClass) {
            applyClassSelection(selectedClass);
        }
    }, [formData.classOfPolicyId, classOfPolicyOptions]);

    // ================= GST & NET PREMIUM AUTO-CALC =================
    useEffect(() => {
        const gross = parseFloat(formData.grossPremium);
        const gstRate = parseFloat(formData.gstPercentage);

        if (!isNaN(gross) && !isNaN(gstRate) && gstRate >= 0) {
            const netPremium = (gross * 100) / (100 + gstRate);
            const gstAmount = gross - netPremium;

            setFormData(prev => ({
                ...prev,
                netPremium: netPremium.toFixed(2),
                gstAmount: gstAmount.toFixed(2)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                netPremium: '',
                gstAmount: ''
            }));
        }
    }, [formData.grossPremium, formData.gstPercentage]);

    const calculateAdjustedBrokeragePercentage = (baseValue, adjustmentType, adjustmentValue) => {
        const baseRate = parseFloat(baseValue);
        const adjustmentRate = parseFloat(adjustmentValue);

        if (Number.isNaN(baseRate)) return '';
        if (Number.isNaN(adjustmentRate)) return baseRate.toFixed(2);

        const adjustedRate = adjustmentType === 'subtract'
            ? baseRate - adjustmentRate
            : baseRate + adjustmentRate;

        return Math.max(adjustedRate, 0).toFixed(2);
    };

    // ================= COMMISSION PERCENTAGE ADJUSTMENT =================
    useEffect(() => {
        const adjustedBrokerage = calculateAdjustedBrokeragePercentage(
            formData.baseBrokeragePercentage,
            formData.commissionAdjustmentType,
            formData.commissionAdjustmentPercentage
        );

        setFormData(prev => ({
            ...prev,
            brokeragePercentage: adjustedBrokerage
        }));
    }, [
        formData.baseBrokeragePercentage,
        formData.commissionAdjustmentType,
        formData.commissionAdjustmentPercentage
    ]);


    // ================= BROKERAGE AUTO-CALC =================
    useEffect(() => {
        const net = parseFloat(formData.netPremium);
        const brokerageRate = parseFloat(formData.brokeragePercentage);

        if (!isNaN(net) && !isNaN(brokerageRate) && brokerageRate >= 0) {
            const totalBrokerage = (net * brokerageRate) / 100;

            setFormData(prev => ({
                ...prev,
                totalBrokerage: totalBrokerage.toFixed(2)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                totalBrokerage: ''
            }));
        }
    }, [formData.netPremium, formData.brokeragePercentage]);


    //=========================Auto-populate when coming from Prospect======================
    useEffect(() => {
        if (location.state?.draftId) return;

        if (location.state?.fromProspect && location.state?.prospectData) {
            const p = location.state.prospectData;

            setIsConverted(true);
            setProspectId(p._id); // ✅ VERY IMPORTANT

            setFormData(prev => ({
                ...prev,
                clientType: p.clientType || 'Retail',
                clientName: p.prospectName || '',
                classOfPolicyId: p.classOfPolicyId || '',
                classOfPolicyName: p.classOfPolicyName || '',
                policyName: p.policyName || '',
                businessType:
                    p.typeOfBusiness === 'RENEWAL'
                        ? 'Renew'
                        : p.typeOfBusiness === 'ROLLOVER'
                            ? 'Roll Over'
                            : 'New',
                grossPremium: p.grossPremium || '',
                policyToBeRenewed: p.typeOfBusiness === 'RENEWAL'
            }));

            setClientInput(p.prospectName || '');
            // ✅ IMPORTANT: clear router state immediately
            navigate(location.pathname, { replace: true });
        }
    }, [location.state, location.pathname, navigate]);

    useEffect(() => {
        const draftId = location.state?.draftId;
        if (!draftId) return;

        const loadDraft = async () => {
            try {
                const res = await api.get(`${API_BASE_URL}/api/add-policy/drafts/${draftId}`);
                setCurrentDraftId(draftId);
                applyPolicyToForm(res.data);
                navigate(location.pathname, { replace: true, state: { draftId } });
            } catch (err) {
                console.error('Failed to load draft policy:', err);
                alert(err?.response?.data?.error || 'Failed to load draft policy');
            }
        };

        loadDraft();
    }, [location.state?.draftId, location.pathname, navigate, applyPolicyToForm]);


    useEffect(() => {
        const autoLoadPolicyDetails = async () => {
            if (!isConverted || !formData.policyName) return;

            try {
                const res = await api.get(
                    `${API_BASE_URL}/api/add-policy/policy-details?policyName=${encodeURIComponent(
                        formData.policyName
                    )}`
                );

                const {
                    defaultGST,
                    defaultBrokeragePercentage
                } = res.data;

                setFormData(prev => ({
                    ...prev,
                    gstPercentage: defaultGST,
                    baseBrokeragePercentage: defaultBrokeragePercentage,
                    brokeragePercentage: calculateAdjustedBrokeragePercentage(
                        defaultBrokeragePercentage,
                        prev.commissionAdjustmentType,
                        prev.commissionAdjustmentPercentage
                    )
                }));
            } catch (err) {
                console.error('Auto load policy details failed', err);
            }
        };

        autoLoadPolicyDetails();
    }, [isConverted, formData.policyName]);



    // ================= CLIENT SELECT HANDLER =================
    const handleClientSuggestionSelect = selectedClientName => {
        setClientInput(selectedClientName);
        setFormData(prev => ({ ...prev, clientName: selectedClientName }));
        setClientSuggestions([]);
        setClientFocused(false);
        setClientSelectedIndex(-1);
    };

    const validateQuickBranchForm = () => {
        const errors = {};

        if (!quickBranchForm.insurerId) {
            errors.insurerId = 'Please select an insurer.';
        }
        if (!quickBranchForm.branchName.trim()) {
            errors.branchName = 'Branch Name is required.';
        }
        if (!quickBranchForm.contactPersonName.trim()) {
            errors.contactPersonName = 'Contact Person Name is required.';
        }
        if (!quickBranchForm.contactNumber.trim()) {
            errors.contactNumber = 'Contact Number is required.';
        } else {
            const digitsOnly = quickBranchForm.contactNumber.replace(/\D/g, '');
            if (!contactNumberRegex.test(quickBranchForm.contactNumber) || digitsOnly.length < 6 || digitsOnly.length > 12) {
                errors.contactNumber = 'Enter a valid mobile or landline number.';
            }
        }
        if (!quickBranchForm.email.trim()) {
            errors.email = 'Email is required.';
        } else if (!emailRegex.test(quickBranchForm.email.trim())) {
            errors.email = 'Enter a valid email address.';
        }
        if (!quickBranchForm.address.trim()) {
            errors.address = 'Address is required.';
        }
        if (!quickBranchForm.pinCode.trim()) {
            errors.pinCode = 'Pin Code is required.';
        } else if (!pinCodeRegex.test(quickBranchForm.pinCode.trim())) {
            errors.pinCode = 'Pin Code must be 6 digits.';
        }
        if (!quickBranchForm.city.trim()) {
            errors.city = 'City is required.';
        }
        if (!quickBranchForm.state.trim()) {
            errors.state = 'State is required.';
        }

        return errors;
    };

    const resolveSelectedInsurer = async insurerName => {
        const normalizedName = String(insurerName || '').trim();
        if (!normalizedName) {
            setSelectedInsurer(null);
            return null;
        }

        try {
            const res = await api.get(
                `${API_BASE_URL}/api/add-policy/insurer-search?query=${encodeURIComponent(normalizedName)}`
            );
            const exactMatch = (Array.isArray(res.data) ? res.data : []).find(
                item => String(item.insurerName || '').toLowerCase() === normalizedName.toLowerCase()
            ) || null;

            setSelectedInsurer(exactMatch);
            return exactMatch;
        } catch (err) {
            console.error('Error resolving insurer:', err);
            setSelectedInsurer(null);
            return null;
        }
    };

    const loadBranchesForInsurer = async insurerName => {
        const normalizedInsurerName = String(insurerName || '').trim();

        if (!normalizedInsurerName) {
            setBranches([]);
            setSelectedInsurer(null);
            setFormData(prev => ({ ...prev, branchName: '' }));
            return;
        }

        await resolveSelectedInsurer(normalizedInsurerName);

        try {
            const res = await api.get(
                `${API_BASE_URL}/api/add-policy/branches?insurerName=${encodeURIComponent(
                    normalizedInsurerName
                )}`
            );

            const branchList = Array.isArray(res.data) ? res.data : [];
            setBranches(branchList);

            setFormData(prev => ({
                ...prev,
                branchName:
                    branchList.length === 1
                        ? branchList[0]
                        : branchList.includes(prev.branchName)
                            ? prev.branchName
                            : ''
            }));
        } catch (err) {
            console.error('Error fetching branches:', err);
            setBranches([]);
            setFormData(prev => ({ ...prev, branchName: '' }));
        }
    };

    // ================= INSURER SELECT HANDLER =================
    const handleSuggestionSelect = async selectedInsurerRow => {
        const selectedName =
            typeof selectedInsurerRow === 'string'
                ? selectedInsurerRow
                : selectedInsurerRow?.insurerName || '';

        setSelectedInsurer(
            typeof selectedInsurerRow === 'string' ? null : selectedInsurerRow || null
        );
        setInsurerInput(selectedName);
        setFormData(prev => ({ ...prev, insurerName: selectedName, branchName: '' }));
        setInsurerSuggestions([]);
        setFocused(false);
        setSelectedIndex(-1);
        await loadBranchesForInsurer(selectedName);
    };

    const openQuickBranchPopup = () => {
        if (!selectedInsurer?._id) {
            return;
        }

        setQuickBranchNotice({ message: '', type: '' });
        setQuickBranchErrors({});
        setQuickBranchForm({
            ...initialQuickBranchForm,
            insurerId: selectedInsurer._id,
            insurerName: selectedInsurer.insurerName
        });
        setShowQuickBranchPopup(true);
    };

    const closeQuickBranchPopup = () => {
        setShowQuickBranchPopup(false);
        setQuickBranchErrors({});
        setQuickBranchForm(initialQuickBranchForm);
        setQuickBranchPinLookupLoading(false);
    };

    const handleQuickBranchInputChange = event => {
        const { name, value } = event.target;
        const sanitizedValue = name === 'pinCode'
            ? value.replace(/\D/g, '').slice(0, 6)
            : name === 'contactNumber'
                ? value.replace(/[^0-9+()\-\s]/g, '').slice(0, 15)
                : value;

        setQuickBranchForm(prev => ({
            ...prev,
            [name]: sanitizedValue
        }));

        setQuickBranchErrors(prev => ({
            ...prev,
            [name]: ''
        }));

        if (name === 'pinCode' && sanitizedValue.length < 6) {
            setQuickBranchForm(prev => ({
                ...prev,
                city: '',
                state: ''
            }));
        }
    };

    const fetchQuickBranchLocation = async pinCode => {
        if (!pinCodeRegex.test(pinCode)) {
            return;
        }

        setQuickBranchPinLookupLoading(true);
        try {
            const response = await api.get(`${API_BASE_URL}/api/insurer-branches/pincode/${pinCode}`);
            setQuickBranchForm(prev => ({
                ...prev,
                city: response.data.city || '',
                state: response.data.state || ''
            }));
            setQuickBranchErrors(prev => ({
                ...prev,
                pinCode: '',
                city: '',
                state: ''
            }));
        } catch (error) {
            setQuickBranchForm(prev => ({
                ...prev,
                city: '',
                state: ''
            }));
            setQuickBranchErrors(prev => ({
                ...prev,
                pinCode: error?.response?.data?.message || 'Unable to fetch City and State for this Pin Code.'
            }));
        } finally {
            setQuickBranchPinLookupLoading(false);
        }
    };

    const handleQuickBranchPinBlur = () => {
        if (quickBranchForm.pinCode.length === 6) {
            fetchQuickBranchLocation(quickBranchForm.pinCode);
        }
    };

    const handleQuickBranchSubmit = async event => {
        event.preventDefault();

        const errors = validateQuickBranchForm();
        if (Object.keys(errors).length > 0) {
            setQuickBranchErrors(errors);
            return;
        }

        setQuickBranchSaving(true);
        try {
            const payload = {
                insurerId: quickBranchForm.insurerId,
                branchName: quickBranchForm.branchName.trim(),
                contactPersonName: quickBranchForm.contactPersonName.trim(),
                contactNumber: quickBranchForm.contactNumber.trim(),
                email: quickBranchForm.email.trim(),
                address: quickBranchForm.address.trim(),
                pinCode: quickBranchForm.pinCode.trim(),
                city: quickBranchForm.city.trim(),
                state: quickBranchForm.state.trim()
            };

            await api.post(`${API_BASE_URL}/api/insurer-branch-requests`, payload);
            setQuickBranchNotice({
                message: 'Branch request submitted for approval. It will be selectable after manager approval.',
                type: 'success'
            });
            closeQuickBranchPopup();
        } catch (error) {
            const responseErrors = error?.response?.data?.errors || {};
            if (Object.keys(responseErrors).length > 0) {
                setQuickBranchErrors(responseErrors);
            }
            if (!responseErrors.branchName && error?.response?.data?.message) {
                setQuickBranchErrors(prev => ({
                    ...prev,
                    branchName: prev.branchName || error.response.data.message
                }));
            }
            setQuickBranchNotice({
                message: error?.response?.data?.message || 'Failed to submit branch request.',
                type: 'error'
            });
        } finally {
            setQuickBranchSaving(false);
        }
    };

    // ================= CHANGE HANDLER =================
    const handleChange = async e => {
        const { name, value, type, checked } = e.target;

        // Policy Name change → load defaults & class/category
        if (name === 'policyName') {
            setFormData(prev => ({
                ...prev,
                policyName: value,
                gstPercentage: '',
                baseBrokeragePercentage: '',
                commissionAdjustmentType: 'add',
                commissionAdjustmentPercentage: '',
                brokeragePercentage: '',
                coverageType: '',
                numberOfLives: ''
            }));

            try {
                const res = await api.get(
                    `${API_BASE_URL}/api/add-policy/policy-details?policyName=${encodeURIComponent(
                        value
                    )}`
                );

                const {
                    defaultGST,
                    defaultBrokeragePercentage
                } = res.data;

                setFormData(prev => ({
                    ...prev,
                    gstPercentage: defaultGST,
                    baseBrokeragePercentage: defaultBrokeragePercentage,
                    brokeragePercentage: calculateAdjustedBrokeragePercentage(
                        defaultBrokeragePercentage,
                        prev.commissionAdjustmentType,
                        prev.commissionAdjustmentPercentage
                    )
                }));
            } catch (err) {
                console.error(
                    'Failed to fetch policy details:',
                    err
                );
            }

            return;
        }

        if (name === 'classOfPolicyId') {
            const selectedClass = classOfPolicyOptions.find(
                item => String(item._id) === String(value)
            );
            if (!selectedClass) return;

            const categoryName = String(selectedClass.categoryName || '').toLowerCase();
            setFormData(prev => ({
                ...prev,
                classOfPolicyId: selectedClass._id || '',
                classOfPolicyName: selectedClass.classOfPolicyName || '',
                policyName: '',
                gstPercentage: '',
                baseBrokeragePercentage: '',
                commissionAdjustmentType: 'add',
                commissionAdjustmentPercentage: '',
                brokeragePercentage: '',
                coverageType: '',
                numberOfLives: ''
            }));

            if (categoryName === 'life') {
                setShowCoverageType(true);
            } else {
                setShowCoverageType(false);
                setShowLivesCovered(false);
            }
            return;
        }

        if (name === 'commissionAdjustmentType') {
            setFormData(prev => ({
                ...prev,
                commissionAdjustmentType: value
            }));
            return;
        }

        if (name === 'commissionAdjustmentPercentage') {
            setFormData(prev => ({
                ...prev,
                commissionAdjustmentPercentage: value
            }));
            return;
        }

        if (name === 'coverageType') {
            setFormData(prev => ({ ...prev, coverageType: value }));

            if (value === 'Group') {
                setShowLivesCovered(true);
            } else {
                setShowLivesCovered(false);
                setFormData(prev => ({ ...prev, numberOfLives: '' }));
            }
            return;
        }

        if (name === 'numberOfLives') {
            setFormData(prev => ({ ...prev, numberOfLives: value }));
            return;
        }

        // Auto-calc policy end date from start date
        if (name === 'policyStartDate' && value) {
            const startDate = new Date(value);
            const endDate = new Date(startDate);
            endDate.setFullYear(startDate.getFullYear() + 1);
            endDate.setDate(endDate.getDate() - 1);

            const formattedEndDate = endDate.toISOString().split('T')[0];

            setFormData(prev => ({
                ...prev,
                [name]: value,
                policyEndDate: formattedEndDate
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // ================= FILE CHANGE =================
    const handleFileChange = e => {
        const file = e.target.files[0];
        setPolicyDoc(file);
        setUploadProgress(file ? 100 : 0);
    };

    const handlePolicyDocDrop = e => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (!file) return;
        setPolicyDoc(file);
        setUploadProgress(100);
        if (policyDocRef.current) {
            const transfer = new DataTransfer();
            transfer.items.add(file);
            policyDocRef.current.files = transfer.files;
        }
    };

    // ================= POLICY NUMBER CHECK =================
    const checkPolicyNumber = async (number) => {
        if (!number.trim()) {
            setPolicyError('');
            setPolicyExists(false);
            return;
        }

        try {
            const encodedNumber = encodeURIComponent(number.trim().toUpperCase());

            const res = await api.get(
                `${API_BASE_URL}/api/add-policy/check-policy/${encodedNumber}`
            );

            if (res.data.exists) {
                setPolicyExists(true);
                setPolicyError('This policy number already exists.');
            } else {
                setPolicyExists(false);
                setPolicyError('');
            }
        } catch (err) {
            console.error('Error checking policy number:', err);
            setPolicyError('Error verifying policy number');
        }
    };


    const validatePolicyNumber = (value) => {
        if (!value) {
            return 'Policy number is required';
        }

        const allowedCharsRegex = /^[A-Z0-9/-]+$/i;

        if (!allowedCharsRegex.test(value)) {
            return 'Only letters, numbers, / and - are allowed';
        }

        if (value.length < 8) {
            return 'Policy number is too short';
        }

        if (value.length > 50) {
            return 'Policy number seems too long';
        }

        return '';
    };

    const scrollToFirstError = () => {
        // 1️⃣ Custom validation error (policy number)
        if (policyError) {
            const el = document.querySelector('[name="policyNumber"]');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.focus();
                return true;
            }
        }

        // 2️⃣ Native HTML5 required field errors
        const firstInvalid = document.querySelector(
            'input:invalid, select:invalid, textarea:invalid'
        );

        if (firstInvalid) {
            firstInvalid.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            firstInvalid.focus();
            return true;
        }

        return false;
    };

    // ================= SUBMIT =================
    const handleSubmit = async e => {
        e.preventDefault();
        setSubmitAttempted(true);

        const hasError = scrollToFirstError();
        if (hasError) return;


        const payload = new FormData();
        Object.entries(formData).forEach(([key, val]) =>
            payload.append(key, val)
        );
        if (policyDoc) payload.append('policyDoc', policyDoc);
        payload.append(
            'policyStatus',
            policyDoc ? 'Policy Doc Uploaded' : 'Policy Doc Pending'
        );
        if (currentDraftId) {
            payload.append('draftId', currentDraftId);
        }

        if (isConverted && prospectId) {
            payload.append('prospectId', prospectId);
        }

        if (policyExists) {
            setPolicyError(
                'This policy number already exists. Please use a different one.'
            );
            return;
        }

        try {
            await api.post(
                `${API_BASE_URL}/api/add-policy/submit`,
                payload,
                {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }
            );

            alert('Policy saved successfully!');

            // Reset form
            setFormData({
                clientType: defaultClientType,
                clientName: '',
                insurerName: '',
                branchName: '',
                transactionDate: '',
                transactionType: '',
                amountReceived: '',
                paymentMode: '',
                businessType: '',
                classOfPolicyId: '',
                classOfPolicyName: '',
                policyName: '',
                coverageType: '',
                numberOfLives: '',
                policyNumber: '',
                policyIssueDate: '',
                policyStartDate: '',
                policyEndDate: '',
                policyToBeRenewed: false,
                grossPremium: '',
                gstPercentage: '',
                gstAmount: '',
                netPremium: '',
                baseBrokeragePercentage: '',
                commissionAdjustmentType: 'add',
                commissionAdjustmentPercentage: '',
                brokeragePercentage: '',
                totalBrokerage: '',
            });

            setPolicyDoc(null);
            setInsurerInput('');
            setClientInput('');
            setPolicyError('');
            setPolicyExists(false);
            setBranches([]);
            setSelectedInsurer(null);
            setShowCoverageType(false);
            setShowLivesCovered(false);
            setCurrentDraftId(null);
            setUploadProgress(0);
            setSubmitAttempted(false);

            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            console.error(error);
            alert('Error submitting policy');
        }
        if (policyDocRef.current) {
            policyDocRef.current.value = '';
        }
        navigate('/add-policy', { replace: true });
    };

    const handleSaveAsDraft = async () => {
        setSavingDraft(true);

        const payload = new FormData();
        Object.entries(formData).forEach(([key, val]) => {
            payload.append(key, val ?? '');
        });
        if (policyDoc) payload.append('policyDoc', policyDoc);
        if (currentDraftId) payload.append('draftId', currentDraftId);

        try {
            const res = await api.post(
                `${API_BASE_URL}/api/add-policy/drafts`,
                payload,
                {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }
            );

            setCurrentDraftId(res.data?.draft?._id || currentDraftId);
            alert('Policy saved as draft successfully!');
        } catch (error) {
            console.error('Error saving draft policy:', error);
            alert(error?.response?.data?.error || 'Error saving draft policy');
        } finally {
            setSavingDraft(false);
        }
    };

    const handleResetForm = () => {
        setFormData({
            clientType: defaultClientType,
            clientName: '',
            insurerName: '',
            branchName: '',
            transactionDate: today,
            transactionType: '',
            amountReceived: '',
            paymentMode: '',
            businessType: '',
            classOfPolicyId: '',
            classOfPolicyName: '',
            policyName: '',
            coverageType: '',
            numberOfLives: '',
            policyNumber: '',
            policyIssueDate: '',
            policyStartDate: '',
            policyEndDate: '',
            policyToBeRenewed: false,
            grossPremium: '',
            gstPercentage: '',
            gstAmount: '',
            netPremium: '',
            baseBrokeragePercentage: '',
            commissionAdjustmentType: 'add',
            commissionAdjustmentPercentage: '',
            brokeragePercentage: '',
            totalBrokerage: ''
        });
        setPolicyDoc(null);
        setUploadProgress(0);
        setInsurerInput('');
        setClientInput('');
        setPolicyError('');
        setPolicyExists(false);
        setBranches([]);
        setSelectedInsurer(null);
        setShowCoverageType(false);
        setShowLivesCovered(false);
        setSubmitAttempted(false);
        if (policyDocRef.current) {
            policyDocRef.current.value = '';
        }
    };

    const resetBulkUploadState = () => {
        setBulkUploadFile(null);
        setBulkUploadStep('select');
        setBulkUploadClientType('');
        setBulkUploadSummary(null);
        setBulkUploadError('');
        setBulkUploadDragActive(false);
    };

    const closeBulkUploadModal = () => {
        setBulkUploadOpen(false);
        resetBulkUploadState();
    };

    const handleBulkFileSelect = (file) => {
        if (!file) return;
        const isAllowed = /\.(xlsx|xls|csv)$/i.test(file.name);
        if (!isAllowed) {
            setBulkUploadError('Please upload an .xlsx, .xls, or .csv file.');
            return;
        }

        setBulkUploadFile(file);
        setBulkUploadSummary(null);
        setBulkUploadError('');
        setBulkUploadStep('select');
    };

    const downloadBulkTemplate = async (clientType = bulkUploadClientType || 'Retail') => {
        try {
            const response = await api.get(`${API_BASE_URL}/api/add-policy/bulk-template`, {
                responseType: 'blob',
                params: { clientType }
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const fileLabel = clientType === 'Both' ? 'Combined' : clientType;
            link.download = `${fileLabel}_Bulk_Policy_Upload_Template.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert(err?.response?.data?.error || 'Unable to download template');
        }
    };

    const validateBulkUpload = async () => {
        if (!bulkUploadFile) {
            setBulkUploadError('Choose an Excel file before validation.');
            return;
        }
        if (!bulkUploadClientType) {
            setBulkUploadError('Select Retail, Corporate, or Retail + Corporate before uploading the file.');
            return;
        }

        setBulkUploadStep('validating');
        setBulkUploadError('');

        const payload = new FormData();
        payload.append('file', bulkUploadFile);
        payload.append('clientType', bulkUploadClientType);

        try {
            const response = await api.post(
                `${API_BASE_URL}/api/add-policy/bulk-validate`,
                payload,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setBulkUploadSummary(response.data);
            setBulkUploadStep('preview');
        } catch (err) {
            setBulkUploadError(err?.response?.data?.error || 'Failed to validate bulk policy file.');
            setBulkUploadStep('select');
        }
    };

    const confirmBulkUpload = async () => {
        const validRows = bulkUploadSummary?.validRows || [];
        if (!validRows.length) {
            setBulkUploadError('No valid rows available to upload.');
            return;
        }

        setBulkUploadStep('uploading');
        setBulkUploadError('');

        try {
            const response = await api.post(`${API_BASE_URL}/api/add-policy/bulk-confirm`, {
                rows: validRows,
                clientType: bulkUploadClientType,
                fileName: bulkUploadSummary?.fileName || bulkUploadFile?.name || ''
            });
            setBulkUploadSummary((current) => ({
                ...current,
                uploadResult: response.data,
                errorReportBase64: response.data?.errorReportBase64 || current?.errorReportBase64 || ''
            }));
            setBulkUploadStep('complete');
        } catch (err) {
            setBulkUploadError(err?.response?.data?.error || 'Failed to upload policies.');
            setBulkUploadStep('preview');
        }
    };

    const downloadBulkErrorReport = () => {
        const base64 = bulkUploadSummary?.uploadResult?.errorReportBase64 || bulkUploadSummary?.errorReportBase64;
        if (!base64) return;

        const binary = window.atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
            bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Bulk_Policy_Upload_Error_Report.xlsx';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const closeBulkDocumentModal = () => {
        if (bulkDocumentUploading) return;
        setBulkDocumentOpen(false);
        setBulkDocumentFiles([]);
        setBulkDocumentResult(null);
        setBulkDocumentError('');
        setBulkDocumentDragActive(false);
    };

    const handleBulkDocumentFiles = (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        if (files.length > 200) {
            setBulkDocumentError('Select a maximum of 200 documents at once.');
            return;
        }
        const invalidFile = files.find((file) => !/\.(pdf|doc|docx|jpg|jpeg|png)$/i.test(file.name));
        if (invalidFile) {
            setBulkDocumentError(`${invalidFile.name} is not a supported policy document.`);
            return;
        }
        const oversizedFile = files.find((file) => file.size > 20 * 1024 * 1024);
        if (oversizedFile) {
            setBulkDocumentError(`${oversizedFile.name} is larger than 20 MB.`);
            return;
        }
        setBulkDocumentFiles(files);
        setBulkDocumentResult(null);
        setBulkDocumentError('');
    };

    const uploadBulkPolicyDocuments = async () => {
        if (!bulkDocumentFiles.length) {
            setBulkDocumentError('Select policy documents before uploading.');
            return;
        }

        const payload = new FormData();
        bulkDocumentFiles.forEach((file) => payload.append('policyDocs', file));
        setBulkDocumentUploading(true);
        setBulkDocumentError('');
        try {
            const response = await api.post(
                `${API_BASE_URL}/api/add-policy/bulk-documents`,
                payload,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setBulkDocumentResult(response.data);
        } catch (error) {
            setBulkDocumentError(error?.response?.data?.error || 'Failed to upload policy documents.');
        } finally {
            setBulkDocumentUploading(false);
        }
    };

    if (!isAuthenticated()) return <Navigate to="/" />;

    return (
        <div className="add-policy-container">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>
            <div className={`add-policy-content ${collapsed ? 'collapsed' : ''}`}>
                <div className="add-policy-sticky-header">
                    <Header />
                </div>

                <div className="add-policy-scroll-area">

                    <section className="add-policy-page">
                        <div className="add-policy-hero">
                            <div>
                                {/* <div className="add-policy-breadcrumb">Transactions &gt; Policy &gt; Add Policy</div> */}
                                <h1>{currentDraftId ? 'Continue Draft Policy' : 'Add Policy'}</h1>
                                {/* <p>Create a clean policy record with client, insurer, premium, and document details in one guided workspace.</p> */}
                            </div>
                            <div className="add-policy-hero-actions">
                                <div className="bulk-policy-actions">
                                    {canBulkPolicyUpload && (
                                        <button
                                            type="button"
                                            className="bulk-policy-btn bulk-policy-primary"
                                            onClick={() => setBulkUploadOpen(true)}
                                        >
                                            <UploadCloud size={17} />
                                            Upload Excel
                                        </button>
                                    )}
                                    {canBulkPolicyUpload && (
                                        <button
                                            type="button"
                                            className="bulk-policy-btn bulk-policy-secondary"
                                            onClick={() => setBulkDocumentOpen(true)}
                                        >
                                            <FileText size={17} />
                                            Upload Documents
                                        </button>
                                    )}
                                                                    </div>
                                <div className="add-policy-progress-card">
                                    <div className="progress-card-top">
                                        <span>Completion</span>
                                        <strong>{formCompletion}%</strong>
                                    </div>
                                    <div className="policy-progress-track">
                                        <span style={{ width: `${formCompletion}%` }} />
                                    </div>
                                    <small>{autoSaveStatus} draft locally</small>
                                </div>
                            </div>
                        </div>

                        <div className="add-policy-stepper" aria-label="Policy creation progress">
                            <span className={formData.clientName ? 'step-complete' : ''}>Client</span>
                            <span className={formData.insurerName ? 'step-complete' : ''}>Insurer</span>
                            <span className={formData.policyNumber ? 'step-complete' : ''}>Policy</span>
                            <span className={formData.grossPremium ? 'step-complete' : ''}>Premium</span>
                            <span className={policyDoc ? 'step-complete' : ''}>Document</span>
                        </div>
                        {currentDraftId && (
                            <div className="draft-policy-banner">
                                You are editing a saved draft. Submit will convert it into a final policy.
                            </div>
                        )}
                        {isConverted && (
                            <div style={{ marginBottom: 10, color: '#0d6efd', fontWeight: 600 }}>
                                Converted from Prospect — key fields are locked
                            </div>
                        )}
                        <form
                            onSubmit={handleSubmit}
                            encType="multipart/form-data"
                            className="retail-policy-form"
                        >
                            <div className="add-policy-form-shell">
                                <section className={`policy-section-card ${openSections.client ? '' : 'is-collapsed'}`}>
                                    <button type="button" className="policy-section-header" onClick={() => toggleSection('client')}>
                                        <span className="section-icon"><UserRound size={19} /></span>
                                        <span>
                                            <strong>Client Details</strong>
                                            <small>Select the policy holder and client segment.</small>
                                        </span>
                                        <ChevronDown className="section-chevron" size={18} />
                                    </button>
                                    <div className="policy-section-body">
                                        {/* ================= CLIENT TYPE (NEW) ================= */}
                                        <div className="retail-form-element">
                                            <label className="retail-form-label">Client Type <span className="required-star">*</span></label>
                                            <div className="client-type-options">
                                                {canAccessRetailPolicies && (
                                                    <label className="client-type-pill">
                                                        <input
                                                            type="radio"
                                                            name="clientType"
                                                            value="Retail"
                                                            checked={formData.clientType === 'Retail'}
                                                            onChange={e => {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    clientType: e.target.value
                                                                }));
                                                                setClientInput('');
                                                                setClientSuggestions([]);
                                                            }}
                                                        />
                                                        {' '}Retail
                                                    </label>
                                                )}

                                                {canAccessCorporatePolicies && (
                                                    <label className="client-type-pill">
                                                        <input
                                                            type="radio"
                                                            name="clientType"
                                                            value="Corporate"
                                                            checked={formData.clientType === 'Corporate'}
                                                            onChange={e => {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    clientType: e.target.value
                                                                }));
                                                                setClientInput('');
                                                                setClientSuggestions([]);
                                                            }}
                                                            disabled={isConverted}
                                                        />
                                                        {' '}Corporate
                                                    </label>
                                                )}
                                            </div>
                                        </div>

                                        {/* ================= CLIENT NAME ================= */}
                                        <div
                                            className="retail-form-element-client"
                                            style={{ position: 'relative' }}
                                        >
                                            <label className="retail-form-label-client">
                                                Client Name <span className="required-star">*</span>
                                            </label>
                                            <div className="retail-input-wrapper-client">
                                                <input
                                                    className="retail-form-input-client"
                                                    type="text"
                                                    name="clientName"
                                                    value={clientInput}
                                                    onChange={e => {
                                                        const value = e.target.value;
                                                        setClientInput(value);
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            clientName: value
                                                        }));
                                                        setClientSelectedIndex(-1);
                                                    }}
                                                    onFocus={() => setClientFocused(true)}
                                                    onBlur={e => {
                                                        if (
                                                            e.relatedTarget &&
                                                            e.relatedTarget.classList &&
                                                            e.relatedTarget.classList.contains(
                                                                'add-client-link'
                                                            )
                                                        )
                                                            return;
                                                        setTimeout(() => {
                                                            setClientSuggestions([]);
                                                            setClientFocused(false);
                                                        }, 200);
                                                    }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'ArrowDown') {
                                                            setClientSelectedIndex(prev =>
                                                                Math.min(
                                                                    prev + 1,
                                                                    clientSuggestions.length - 1
                                                                )
                                                            );
                                                        } else if (e.key === 'ArrowUp') {
                                                            setClientSelectedIndex(prev =>
                                                                Math.max(prev - 1, 0)
                                                            );
                                                        } else if (
                                                            e.key === 'Enter' &&
                                                            clientSelectedIndex !== -1
                                                        ) {
                                                            const selectedItem =
                                                                clientSuggestions[clientSelectedIndex];

                                                            const selectedName =
                                                                formData.clientType === 'Retail'
                                                                    ? selectedItem.clientName
                                                                    : selectedItem.companyName;

                                                            handleClientSuggestionSelect(
                                                                selectedName
                                                            );
                                                        }
                                                    }}
                                                    autoComplete="off"
                                                    required
                                                    disabled={isConverted}
                                                />

                                                {clientFocused && (
                                                    <ul className="retail-suggestions">
                                                        {clientSuggestions.map((item, idx) => (
                                                            <li
                                                                key={idx}
                                                                className={
                                                                    idx === clientSelectedIndex
                                                                        ? 'highlighted'
                                                                        : ''
                                                                }
                                                                onMouseDown={() =>
                                                                    handleClientSuggestionSelect(
                                                                        formData.clientType ===
                                                                            'Retail'
                                                                            ? item.clientName
                                                                            : item.companyName
                                                                    )
                                                                }
                                                            >
                                                                {formData.clientType ===
                                                                    'Retail' ? (
                                                                    <>
                                                                        <b>Name:</b>{' '}
                                                                        {item.clientName}
                                                                        <br />
                                                                        <b>Ph NO:</b>{' '}
                                                                        {item.phoneNumber}
                                                                        <br />
                                                                        <b>Aadhar:</b>{' '}
                                                                        {item.aadharNumber}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <b>Client ID:</b>{' '}
                                                                        {item.autoGeneratedId || '-'}
                                                                        <br />
                                                                        <b>Company:</b>{' '}
                                                                        {item.companyName}
                                                                        <br />
                                                                        <b>Contact:</b>{' '}
                                                                        {item.contactNo}
                                                                        <br />
                                                                        <b>Email:</b>{' '}
                                                                        {item.email}
                                                                    </>
                                                                )}
                                                            </li>
                                                        ))}

                                                        <li className="add-new-client-link">
                                                            <a
                                                                href={
                                                                    formData.clientType ===
                                                                        'Retail'
                                                                        ? '/retail/clients-form'
                                                                        : '/corporate/clients-form'
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="add-client-link"
                                                            >
                                                                + Add New {formData.clientType}{' '}
                                                                Client
                                                            </a>
                                                        </li>
                                                    </ul>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                </section>

                                <section className={`policy-section-card ${openSections.insurer ? '' : 'is-collapsed'}`}>
                                    <button type="button" className="policy-section-header" onClick={() => toggleSection('insurer')}>
                                        <span className="section-icon"><Building2 size={19} /></span>
                                        <span>
                                            <strong>Insurer Details</strong>
                                            <small>Choose the insurer and approved branch.</small>
                                        </span>
                                        <ChevronDown className="section-chevron" size={18} />
                                    </button>
                                    <div className="policy-section-body">

                                        {/* ================= INSURER NAME ================= */}
                                        <div
                                            className="retail-form-element-insurer"
                                            style={{ position: 'relative' }}
                                        >
                                            <label className="retail-form-label-insurer">
                                                Insurance Company Name <span className="required-star">*</span>
                                            </label>
                                            <div className="retail-input-wrapper">
                                                <input
                                                    className="retail-form-input-insurer"
                                                    type="text"
                                                    name="insurerName"
                                                    value={insurerInput}
                                                    onChange={e => {
                                                        const value = e.target.value;
                                                        setInsurerInput(value);
                                                        setBranches([]);
                                                        setSelectedInsurer(null);
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            insurerName: value,
                                                            branchName: ''
                                                        }));
                                                        setSelectedIndex(-1);
                                                    }}
                                                    onFocus={() => setFocused(true)}
                                                    onBlur={() => {
                                                        setTimeout(() => {
                                                            setInsurerSuggestions([]);
                                                            setFocused(false);
                                                        }, 200);
                                                    }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'ArrowDown') {
                                                            setSelectedIndex(prev =>
                                                                Math.min(
                                                                    prev + 1,
                                                                    insurerSuggestions.length - 1
                                                                )
                                                            );
                                                        } else if (e.key === 'ArrowUp') {
                                                            setSelectedIndex(prev =>
                                                                Math.max(prev - 1, 0)
                                                            );
                                                        } else if (
                                                            e.key === 'Enter' &&
                                                            selectedIndex !== -1
                                                        ) {
                                                            handleSuggestionSelect(
                                                                insurerSuggestions[selectedIndex]
                                                                    .insurerName
                                                            );
                                                        }
                                                    }}
                                                    autoComplete="off"
                                                    required
                                                />

                                                {focused &&
                                                    insurerSuggestions.length > 0 && (
                                                        <ul className="retail-suggestions">
                                                            {insurerSuggestions.map(
                                                                (item, idx) => (
                                                                    <li
                                                                        key={idx}
                                                                        className={
                                                                            idx === selectedIndex
                                                                                ? 'highlighted'
                                                                                : ''
                                                                        }
                                                                        onMouseDown={() =>
                                                                            handleSuggestionSelect(
                                                                                item
                                                                            )
                                                                        }
                                                                    >
                                                                        {item.insurerName}
                                                                    </li>
                                                                )
                                                            )}
                                                        </ul>
                                                    )}
                                            </div>
                                        </div>

                                        {/* ================= BRANCH NAME ================= */}
                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Insurance Company Branch Name <span className="required-star">*</span>
                                            </label>
                                            <div className="retail-branch-select-row">
                                                <select
                                                    className="retail-select"
                                                    name="branchName"
                                                    value={formData.branchName}
                                                    onChange={handleChange}
                                                    disabled={!selectedInsurer?._id || branches.length === 0}
                                                    required
                                                >
                                                    <option value="">
                                                        {!selectedInsurer?._id
                                                            ? ''
                                                            : branches.length === 0
                                                                ? 'No Branches Found'
                                                                : 'Select Branch'}
                                                    </option>
                                                    {branches.map((branch, index) => (
                                                        <option key={index} value={branch}>
                                                            {branch}
                                                        </option>
                                                    ))}
                                                </select>

                                                <button
                                                    type="button"
                                                    className="branch-add-btn"
                                                    onClick={openQuickBranchPopup}
                                                    disabled={!selectedInsurer?._id}
                                                >
                                                    Add Branch
                                                </button>
                                                {branchInfoMessages.length > 0 && (
                                                    <div className="branch-info-wrap">
                                                        <button
                                                            type="button"
                                                            className="branch-info-btn"
                                                            aria-label="Show branch information"
                                                        >
                                                            i
                                                        </button>
                                                        <div className="branch-info-tooltip">
                                                            {branchInfoMessages.map((item, index) => (
                                                                <small
                                                                    key={`${item.type}-${index}`}
                                                                    className={`branch-info-text branch-info-text-${item.type}`}
                                                                >
                                                                    {item.text}
                                                                </small>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                </section>

                                <section className={`policy-section-card ${openSections.policy ? '' : 'is-collapsed'}`}>
                                    <button type="button" className="policy-section-header" onClick={() => toggleSection('policy')}>
                                        <span className="section-icon"><ShieldCheck size={19} /></span>
                                        <span>
                                            <strong>Policy Details</strong>
                                            <small>Capture policy, business, payment, and period information.</small>
                                        </span>
                                        <ChevronDown className="section-chevron" size={18} />
                                    </button>
                                    <div className="policy-section-body">

                                        {/* ================= OTHER FIELDS (unchanged) ================= */}

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Premium Transaction Date <span className="required-star">*</span>
                                            </label>
                                            <input
                                                className="retail-form-input"
                                                type="date"
                                                name="transactionDate"
                                                value={formData.transactionDate}
                                                max={new Date().toISOString().split('T')[0]}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        {/* <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Transaction Type <span className="required-star">*</span>
                                            </label>
                                            <select
                                                className="retail-select"
                                                name="transactionType"
                                                value={formData.transactionType}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">
                                                    Select Transaction Type
                                                </option>
                                                <option value="Payment Received">
                                                    Payment Received
                                                </option>
                                                <option value="Payment Refund">
                                                    Payment Refund
                                                </option>
                                            </select>
                                        </div> */}

                                        {/* <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Amount Received from Client
                                            </label>
                                            <input
                                                className="retail-form-input"
                                                type="number"
                                                name="amountReceived"
                                                value={formData.amountReceived}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div> */}

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Mode of Payment <span className="required-star">*</span>
                                            </label>
                                            <select
                                                className="retail-select"
                                                name="paymentMode"
                                                value={formData.paymentMode}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">
                                                    Select Payment Mode
                                                </option>
                                                <option value="Cash">Cash</option>
                                                <option value="UPI">UPI</option>
                                                <option value="Cheque">Cheque</option>
                                                <option value="Bank Transfers">
                                                    Bank Transfers
                                                </option>
                                                <option value="Netbanking">Netbanking</option>
                                            </select>
                                        </div>

                                        <br />

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Type of Business <span className="required-star">*</span>
                                            </label>
                                            <select
                                                className="retail-select"
                                                name="businessType"
                                                value={formData.businessType}
                                                onChange={handleChange}
                                                disabled={isConverted}
                                                required
                                            >
                                                <option value="">
                                                    Select Type of Business
                                                </option>
                                                <option value="New">New</option>
                                                <option value="Roll Over">I-Pro Renewal</option>
                                                <option value="Renew">Other Renewal</option>
                                            </select>
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Class of Policy <span className="required-star">*</span>
                                            </label>
                                            <select
                                                className="retail-select"
                                                name="classOfPolicyId"
                                                value={formData.classOfPolicyId}
                                                onChange={handleChange}
                                                disabled={isConverted}
                                                required
                                            >
                                                <option value="">Select Class of Policy</option>
                                                {classOfPolicyOptions.map(item => (
                                                    <option
                                                        key={item._id}
                                                        value={item._id}
                                                        style={{ fontWeight: 400, fontSize: '14px' }}
                                                    >
                                                        {item.policyType
                                                            ? `${item.policyType}, ${item.classOfPolicyName}`
                                                            : item.classOfPolicyName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Policy Name <span className="required-star">*</span>
                                            </label>
                                            <select
                                                className="retail-select policy-dropdown"
                                                name="policyName"
                                                value={formData.policyName}
                                                onChange={handleChange}
                                                disabled={isConverted || !formData.classOfPolicyId}
                                                required
                                            >
                                                <option value="">
                                                    {formData.classOfPolicyId
                                                        ? 'Select Policy Name'
                                                        : 'Select Class of Policy First'}
                                                </option>
                                                {policyNames.map((name, idx) => (
                                                    <option key={idx} value={name}>
                                                        {name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {showCoverageType && (
                                            <div className="retail-form-element">
                                                <label className="retail-form-label">
                                                    Coverage Type <span className="required-star">*</span>
                                                </label>
                                                <select
                                                    className="retail-select"
                                                    name="coverageType"
                                                    value={formData.coverageType}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="">
                                                        Select Coverage Type
                                                    </option>
                                                    <option value="Individual">
                                                        Individual
                                                    </option>
                                                    <option value="Group">Group</option>
                                                </select>
                                            </div>
                                        )}

                                        {showLivesCovered && (
                                            <div className="retail-form-element">
                                                <label className="retail-form-label">
                                                    Number of Lives Covered <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    className="retail-form-input"
                                                    type="number"
                                                    name="numberOfLives"
                                                    value={formData.numberOfLives}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        )}

                                        <div
                                            className="retail-form-element"
                                            style={{ position: 'relative' }}
                                        >
                                            <label className="retail-form-label">
                                                Policy Number <span className="required-star">*</span>
                                            </label>
                                            <input
                                                className="retail-form-input"
                                                type="text"
                                                name="policyNumber"
                                                value={formData.policyNumber}
                                                onChange={e => {
                                                    const value = e.target.value.toUpperCase();

                                                    handleChange({
                                                        target: {
                                                            name: 'policyNumber',
                                                            value
                                                        }
                                                    });

                                                    // 1️⃣ Local validation
                                                    const errorMsg = validatePolicyNumber(value);
                                                    setPolicyError(errorMsg);

                                                    // reset duplicate state when typing
                                                    setPolicyExists(false);

                                                    // 2️⃣ API duplicate check (only if locally valid)
                                                    clearTimeout(window.policyCheckTimer);

                                                    if (!errorMsg) {
                                                        window.policyCheckTimer = setTimeout(() => {
                                                            checkPolicyNumber(value);
                                                        }, 400);
                                                    }
                                                }}
                                                required
                                            />

                                            {policyError && (
                                                <small
                                                    style={{
                                                        color: 'red',
                                                        position: 'absolute',
                                                        bottom: '-18px',
                                                        left: '5px'
                                                    }}
                                                >
                                                    {policyError}
                                                </small>
                                            )}
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Policy Issue Date <span className="required-star">*</span>
                                            </label>
                                            <input
                                                className="retail-form-input"
                                                type="date"
                                                name="policyIssueDate"
                                                value={formData.policyIssueDate}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Policy Start Date <span className="required-star">*</span>
                                            </label>
                                            <input
                                                className="retail-form-input"
                                                type="date"
                                                name="policyStartDate"
                                                value={formData.policyStartDate}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Policy End Date <span className="required-star">*</span>
                                            </label>
                                            <input
                                                className="retail-form-input"
                                                type="date"
                                                name="policyEndDate"
                                                value={formData.policyEndDate}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                    </div>
                                </section>

                                <section className={`policy-section-card ${openSections.premium ? '' : 'is-collapsed'}`}>
                                    <button type="button" className="policy-section-header" onClick={() => toggleSection('premium')}>
                                        <span className="section-icon"><CircleDollarSign size={19} /></span>
                                        <span>
                                            <strong>Premium Details</strong>
                                            <small>Review calculated GST, net premium, and brokerage values.</small>
                                        </span>
                                        <ChevronDown className="section-chevron" size={18} />
                                    </button>
                                    <div className="policy-section-body premium-preview-grid">

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Gross Premium <span className="required-star">*</span>
                                            </label>
                                            <input
                                                className="retail-form-input no-spinner"
                                                type="number"
                                                name="grossPremium"
                                                value={formData.grossPremium}
                                                onChange={handleChange}
                                                // disabled={isConverted}
                                                required
                                            />
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                GST Percentage
                                            </label>
                                            <input
                                                className="retail-form-input"
                                                type="text"
                                                name="gstPercentage"
                                                value={formData.gstPercentage}
                                                readOnly
                                                required
                                            />
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                GST Amount
                                            </label>
                                            <input
                                                className="retail-form-input"
                                                type="text"
                                                name="gstAmount"
                                                value={formData.gstAmount}
                                                readOnly
                                                required
                                            />
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Net Premium
                                            </label>
                                            <input
                                                className="retail-form-input"
                                                type="text"
                                                name="netPremium"
                                                value={formData.netPremium}
                                                readOnly
                                                required
                                            />
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Default Commission Percentage
                                            </label>
                                            <input
                                                className="retail-form-input"
                                                type="text"
                                                name="baseBrokeragePercentage"
                                                value={formData.baseBrokeragePercentage}
                                                readOnly
                                            />
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Commission Adjustment
                                            </label>
                                            <select
                                                className="retail-select"
                                                name="commissionAdjustmentType"
                                                value={formData.commissionAdjustmentType}
                                                onChange={handleChange}
                                            >
                                                <option value="add">Add</option>
                                                <option value="subtract">Subtract</option>
                                            </select>
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Adjustment Percentage
                                            </label>
                                            <input
                                                className="retail-form-input no-spinner"
                                                type="number"
                                                name="commissionAdjustmentPercentage"
                                                value={formData.commissionAdjustmentPercentage}
                                                onChange={handleChange}
                                                min="0"
                                                step="0.01"
                                                placeholder="0.00"
                                            />
                                        </div>

                                        <div className="retail-form-element">
                                            <label className="retail-form-label">
                                                Final Commission Percentage
                                            </label>
                                            <input
                                                className="retail-form-input"
                                                type="text"
                                                name="brokeragePercentage"
                                                value={formData.brokeragePercentage}
                                                readOnly
                                                required
                                            />
                                        </div>

                                        {isAdmin && (
                                            <div className="retail-form-element">
                                                <label className="retail-form-label">
                                                    Total Brokerage Amount Receivable
                                                </label>
                                                <input
                                                    className="retail-form-input"
                                                    type="text"
                                                    name="totalBrokerage"
                                                    value={formData.totalBrokerage}
                                                    readOnly
                                                    required
                                                />
                                            </div>
                                        )}

                                    </div>
                                </section>

                                <section className={`policy-section-card ${openSections.document ? '' : 'is-collapsed'}`}>
                                    <button type="button" className="policy-section-header" onClick={() => toggleSection('document')}>
                                        <span className="section-icon"><FileText size={19} /></span>
                                        <span>
                                            <strong>Document Upload</strong>
                                            <small>Attach the policy document now or leave it pending.</small>
                                        </span>
                                        <ChevronDown className="section-chevron" size={18} />
                                    </button>
                                    <div className="policy-section-body policy-document-grid">

                                        {/* <div className="retail-form-element">
                            <label className="retail-form-label">
                                Policy To Be Renewed
                            </label>
                            <input
                                type="checkbox"
                                name="policyToBeRenewed"
                                checked={formData.policyToBeRenewed}
                                onChange={handleChange}
                            />
                        </div> */}

                                        <div className="retail-form-element document-upload-field">
                                            <label className="retail-form-label">Policy Document</label>
                                            <input
                                                id="policyDocUpload"
                                                className="policy-file-input"
                                                type="file"
                                                name="policyDoc"
                                                ref={policyDocRef}
                                                onChange={handleFileChange}
                                            />
                                            <label
                                                className={`policy-dropzone ${policyDoc ? 'has-file' : ''}`}
                                                htmlFor="policyDocUpload"
                                                onDrop={handlePolicyDocDrop}
                                                onDragOver={e => e.preventDefault()}
                                            >
                                                <span className="upload-icon"><UploadCloud size={30} /></span>
                                                <strong>{policyDoc ? policyDoc.name : 'Drag & drop policy document'}</strong>
                                                <small>{policyDoc ? formatFileSize(policyDoc.size) : 'PDF, image, or office document supported'}</small>
                                                {policyDoc && (
                                                    <div className="upload-progress">
                                                        <span style={{ width: `${uploadProgress}%` }} />
                                                    </div>
                                                )}
                                            </label>
                                        </div>

                                        <div className="policy-summary-preview">
                                            <div className="summary-preview-title">
                                                <ClipboardCheck size={18} />
                                                Summary Preview
                                            </div>
                                            <p>{policySummary || 'Start filling the form to preview the policy summary.'}</p>
                                            {submitAttempted && policyError && (
                                                <small className="field-error">{policyError}</small>
                                            )}
                                        </div>

                                    </div>
                                </section>

                                <div className="policy-form-actions">
                                    <button type="button" className="reset-policy-btn" onClick={handleResetForm}>
                                        <RotateCcw size={18} />
                                        Reset
                                    </button>
                                    <button type="button" className="save-draft-btn" onClick={handleSaveAsDraft} disabled={savingDraft}>
                                        <Save size={18} />
                                        {savingDraft ? 'Saving Draft...' : 'Save as Draft'}
                                    </button>
                                    <button type="submit" className="submit-policy-btn">
                                        <Send size={18} />
                                        Submit Policy
                                    </button>
                                </div>
                            </div>
                        </form>

                        {showQuickBranchPopup && (
                            <div className="quick-branch-popup-overlay">
                                <div className="quick-branch-popup">
                                    <button
                                        type="button"
                                        className="quick-branch-popup-close"
                                        onClick={closeQuickBranchPopup}
                                    >
                                        x
                                    </button>
                                    <h3>Add Insurer Branch</h3>

                                    <form className="quick-branch-form" onSubmit={handleQuickBranchSubmit}>
                                        <div className="quick-branch-field full-width">
                                            <label>Insurer</label>
                                            <input type="text" value={quickBranchForm.insurerName} readOnly />
                                        </div>

                                        <div className="quick-branch-field">
                                            <label>Branch Name</label>
                                            <input
                                                type="text"
                                                name="branchName"
                                                value={quickBranchForm.branchName}
                                                onChange={handleQuickBranchInputChange}
                                            />
                                            {quickBranchErrors.branchName && <span className="quick-branch-error">{quickBranchErrors.branchName}</span>}
                                        </div>

                                        <div className="quick-branch-field">
                                            <label>Contact Person Name</label>
                                            <input
                                                type="text"
                                                name="contactPersonName"
                                                value={quickBranchForm.contactPersonName}
                                                onChange={handleQuickBranchInputChange}
                                            />
                                            {quickBranchErrors.contactPersonName && <span className="quick-branch-error">{quickBranchErrors.contactPersonName}</span>}
                                        </div>

                                        <div className="quick-branch-field">
                                            <label>Contact Number</label>
                                            <input
                                                type="text"
                                                name="contactNumber"
                                                value={quickBranchForm.contactNumber}
                                                onChange={handleQuickBranchInputChange}
                                            />
                                            {quickBranchErrors.contactNumber && <span className="quick-branch-error">{quickBranchErrors.contactNumber}</span>}
                                        </div>

                                        <div className="quick-branch-field">
                                            <label>Email</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={quickBranchForm.email}
                                                onChange={handleQuickBranchInputChange}
                                            />
                                            {quickBranchErrors.email && <span className="quick-branch-error">{quickBranchErrors.email}</span>}
                                        </div>

                                        <div className="quick-branch-field full-width">
                                            <label>Address</label>
                                            <textarea
                                                name="address"
                                                rows="3"
                                                value={quickBranchForm.address}
                                                onChange={handleQuickBranchInputChange}
                                            />
                                            {quickBranchErrors.address && <span className="quick-branch-error">{quickBranchErrors.address}</span>}
                                        </div>

                                        <div className="quick-branch-field">
                                            <label>Pin Code</label>
                                            <input
                                                type="text"
                                                name="pinCode"
                                                value={quickBranchForm.pinCode}
                                                onChange={handleQuickBranchInputChange}
                                                onBlur={handleQuickBranchPinBlur}
                                            />
                                            {quickBranchPinLookupLoading && <span className="quick-branch-hint">Fetching city and state...</span>}
                                            {quickBranchErrors.pinCode && <span className="quick-branch-error">{quickBranchErrors.pinCode}</span>}
                                        </div>

                                        <div className="quick-branch-field">
                                            <label>City</label>
                                            <input type="text" name="city" value={quickBranchForm.city} readOnly />
                                            {quickBranchErrors.city && <span className="quick-branch-error">{quickBranchErrors.city}</span>}
                                        </div>

                                        <div className="quick-branch-field">
                                            <label>State</label>
                                            <input type="text" name="state" value={quickBranchForm.state} readOnly />
                                            {quickBranchErrors.state && <span className="quick-branch-error">{quickBranchErrors.state}</span>}
                                        </div>

                                        <div className="quick-branch-actions full-width">
                                            <button type="button" className="quick-branch-cancel-btn" onClick={closeQuickBranchPopup}>
                                                Cancel
                                            </button>
                                            <button type="submit" className="quick-branch-save-btn" disabled={quickBranchSaving}>
                                                {quickBranchSaving ? 'Saving...' : 'Save Branch'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {bulkUploadOpen && (
                            <div className="bulk-policy-modal-overlay" onClick={closeBulkUploadModal}>
                                <div className="bulk-policy-modal" onClick={(e) => e.stopPropagation()}>
                                    <div className="bulk-policy-modal-head">
                                        <div>
                                            <h3>Bulk Policy Upload</h3>
                                            <p>Validate issued policy records before saving them to the policy register.</p>
                                        </div>
                                        <button type="button" onClick={closeBulkUploadModal}>x</button>
                                    </div>

                                    <div className="bulk-policy-modal-body">
                                        <div className="bulk-policy-steps">
                                            <span className={bulkUploadClientType ? 'is-complete' : 'is-active'}>Type</span>
                                            <span className={bulkUploadStep !== 'select' ? 'is-complete' : bulkUploadClientType ? 'is-active' : ''}>File</span>
                                            <span className={['preview', 'uploading', 'complete'].includes(bulkUploadStep) ? 'is-complete' : ''}>Preview</span>
                                            <span className={bulkUploadStep === 'complete' ? 'is-complete' : ''}>Upload</span>
                                        </div>

                                        <div className="bulk-policy-type-panel">
                                            <div>
                                                <h4>Select Policy Segment</h4>
                                                <p>Choose one segment, or upload Retail and Corporate policies together.</p>
                                            </div>
                                            <div className="bulk-policy-type-options">
                                                <button
                                                    type="button"
                                                    className={bulkUploadClientType === 'Retail' ? 'is-selected' : ''}
                                                    disabled={!canBulkRetailPolicyUpload}
                                                    onClick={() => {
                                                        setBulkUploadClientType('Retail');
                                                        setBulkUploadFile(null);
                                                        setBulkUploadSummary(null);
                                                        setBulkUploadError('');
                                                    }}
                                                >
                                                    Retail
                                                </button>
                                                <button
                                                    type="button"
                                                    className={bulkUploadClientType === 'Corporate' ? 'is-selected' : ''}
                                                    disabled={!canBulkCorporatePolicyUpload}
                                                    onClick={() => {
                                                        setBulkUploadClientType('Corporate');
                                                        setBulkUploadFile(null);
                                                        setBulkUploadSummary(null);
                                                        setBulkUploadError('');
                                                    }}
                                                >
                                                    Corporate
                                                </button>
                                                <button
                                                    type="button"
                                                    className={bulkUploadClientType === 'Both' ? 'is-selected' : ''}
                                                    disabled={!canBulkCombinedPolicyUpload}
                                                    onClick={() => {
                                                        setBulkUploadClientType('Both');
                                                        setBulkUploadFile(null);
                                                        setBulkUploadSummary(null);
                                                        setBulkUploadError('');
                                                    }}
                                                    title={canBulkCombinedPolicyUpload ? 'Upload Retail and Corporate rows in one Excel file' : 'Both Retail and Corporate upload privileges are required'}
                                                >
                                                    Retail + Corporate
                                                </button>
                                            </div>
                                        </div>

                                        {canDownloadBulkPolicyTemplate && (
                                            <div className="bulk-policy-template-row">
                                                <button type="button" onClick={() => downloadBulkTemplate('Retail')}>
                                                    <Download size={15} />
                                                    Retail Template
                                                </button>
                                                <button type="button" onClick={() => downloadBulkTemplate('Corporate')}>
                                                    <Download size={15} />
                                                    Corporate Template
                                                </button>
                                                <button type="button" onClick={() => downloadBulkTemplate('Both')}>
                                                    <Download size={15} />
                                                    Combined Template
                                                </button>
                                            </div>
                                        )}

                                        <label
                                            className={`bulk-policy-dropzone ${bulkUploadDragActive ? 'is-dragging' : ''} ${bulkUploadFile ? 'has-file' : ''} ${!bulkUploadClientType ? 'is-disabled' : ''}`}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                if (!bulkUploadClientType) return;
                                                setBulkUploadDragActive(true);
                                            }}
                                            onDragLeave={() => setBulkUploadDragActive(false)}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                setBulkUploadDragActive(false);
                                                if (!bulkUploadClientType) {
                                                    setBulkUploadError('Select Retail, Corporate, or Retail + Corporate before uploading the file.');
                                                    return;
                                                }
                                                handleBulkFileSelect(e.dataTransfer.files?.[0]);
                                            }}
                                        >
                                            <input
                                                type="file"
                                                accept=".xlsx,.xls,.csv"
                                                disabled={!bulkUploadClientType}
                                                onChange={(e) => handleBulkFileSelect(e.target.files?.[0])}
                                            />
                                            <UploadCloud size={34} />
                                            <strong>
                                                {bulkUploadFile
                                                    ? bulkUploadFile.name
                                                    : bulkUploadClientType
                                                        ? `Drop ${bulkUploadClientType === 'Both' ? 'combined Retail + Corporate' : bulkUploadClientType} Excel file here`
                                                        : 'Select a policy segment first'}
                                            </strong>
                                            <small>.xlsx, .xls, and .csv files are supported</small>
                                        </label>

                                        {bulkUploadStep === 'validating' || bulkUploadStep === 'uploading' ? (
                                            <div className="bulk-policy-processing">
                                                <span />
                                                <strong>{bulkUploadStep === 'validating' ? 'Validating rows...' : 'Uploading valid policies...'}</strong>
                                            </div>
                                        ) : null}

                                        {bulkUploadSummary && (
                                            <>
                                                <div className="bulk-policy-result-grid">
                                                    <div>
                                                        <span>Total Rows</span>
                                                        <strong>{bulkUploadSummary.uploadResult?.totalRows ?? bulkUploadSummary.totalRows}</strong>
                                                    </div>
                                                    <div className="success">
                                                        <span>Success</span>
                                                        <strong>{bulkUploadSummary.uploadResult?.successCount ?? bulkUploadSummary.validRows?.length ?? 0}</strong>
                                                    </div>
                                                    <div className="failed">
                                                        <span>Failed</span>
                                                        <strong>{bulkUploadSummary.uploadResult?.failedCount ?? bulkUploadSummary.invalidRows?.length ?? 0}</strong>
                                                    </div>
                                                    {bulkUploadSummary.uploadResult && (
                                                        <div className="success">
                                                            <span>Clients Created</span>
                                                            <strong>{bulkUploadSummary.uploadResult.createdClientCount ?? 0}</strong>
                                                        </div>
                                                    )}
                                                </div>

                                                {(bulkUploadSummary.invalidRows || []).length > 0 && (
                                                    <div className="bulk-policy-preview bulk-policy-error-preview">
                                                        <div className="bulk-policy-preview-head">
                                                            <h4>Validation Errors</h4>
                                                            {(bulkUploadSummary.errorReportBase64 || bulkUploadSummary.uploadResult?.errorReportBase64) && (
                                                                <button type="button" onClick={downloadBulkErrorReport}>
                                                                    <Download size={15} />
                                                                    Error Report
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="bulk-policy-preview-table-wrap">
                                                            <table>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Row</th>
                                                                        <th>Policy No</th>
                                                                        <th>Client</th>
                                                                        <th>Error Reason</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(bulkUploadSummary.invalidRows || []).map((row) => (
                                                                        <tr key={`error-${row.rowNumber}`}>
                                                                            <td>{row.rowNumber}</td>
                                                                            <td>{row.policyNumber || '-'}</td>
                                                                            <td>{row.row?.clientName || '-'}</td>
                                                                            <td>
                                                                                <div className="bulk-policy-error-list">
                                                                                    {(row.errors || ['Invalid row']).map((errorText) => (
                                                                                        <span key={`${row.rowNumber}-${errorText}`}>{errorText}</span>
                                                                                    ))}
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="bulk-policy-preview">
                                                    <div className="bulk-policy-preview-head">
                                                        <h4>Valid Rows Preview</h4>
                                                        {(bulkUploadSummary.errorReportBase64 || bulkUploadSummary.uploadResult?.errorReportBase64) && (
                                                            <button type="button" onClick={downloadBulkErrorReport}>
                                                                <Download size={15} />
                                                                Error Report
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="bulk-policy-preview-table-wrap">
                                                        <table>
                                                            <thead>
                                                                <tr>
                                                                    <th>Row</th>
                                                                    <th>Policy No</th>
                                                                    <th>Client</th>
                                                                    <th>Insurer</th>
                                                                    <th>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(bulkUploadSummary.validRows || []).slice(0, 8).map((row) => (
                                                                    <tr key={`valid-${row.rowNumber}`}>
                                                                        <td>{row.rowNumber}</td>
                                                                        <td>{row.policyNumber}</td>
                                                                        <td>{row.clientName}</td>
                                                                        <td>{row.insurerName}</td>
                                                                        <td>
                                                                            <span className="bulk-row-valid">
                                                                                {row.createCorporateClient || row.createRetailClient
                                                                                    ? 'Valid - New client'
                                                                                    : 'Valid'}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                                {!(bulkUploadSummary.validRows || []).length && (
                                                                    <tr>
                                                                        <td colSpan="5" className="bulk-policy-table-empty">
                                                                            No valid rows found in this file.
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {bulkUploadError && <div className="bulk-policy-error">{bulkUploadError}</div>}
                                    </div>

                                    <div className="bulk-policy-modal-actions">
                                        <button type="button" className="bulk-policy-cancel" onClick={closeBulkUploadModal}>
                                            Cancel
                                        </button>
                                        {bulkUploadStep === 'preview' ? (
                                            <button
                                                type="button"
                                                className="bulk-policy-confirm"
                                                onClick={confirmBulkUpload}
                                                disabled={!bulkUploadSummary?.validRows?.length}
                                            >
                                                Confirm Upload
                                            </button>
                                        ) : bulkUploadStep === 'complete' ? (
                                            <button type="button" className="bulk-policy-confirm" onClick={closeBulkUploadModal}>
                                                Done
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="bulk-policy-confirm"
                                                onClick={validateBulkUpload}
                                                disabled={!bulkUploadClientType || !bulkUploadFile || bulkUploadStep === 'validating' || bulkUploadStep === 'uploading'}
                                            >
                                                Validate File
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {bulkDocumentOpen && (
                            <div className="bulk-policy-modal-overlay" onClick={closeBulkDocumentModal}>
                                <div className="bulk-policy-modal" onClick={(event) => event.stopPropagation()}>
                                    <div className="bulk-policy-modal-head">
                                        <div>
                                            <h3>Bulk Policy Document Upload</h3>
                                            <p>Select documents whose filenames are the corresponding policy numbers.</p>
                                        </div>
                                        <button type="button" onClick={closeBulkDocumentModal} disabled={bulkDocumentUploading}>x</button>
                                    </div>

                                    <div className="bulk-policy-modal-body">
                                        <div className="bulk-policy-type-panel">
                                            <div>
                                                <h4>Document naming</h4>
                                                <p>
                                                    Example: policy <b>POL/2026/001</b> can use <b>POL-2026-001.pdf</b>.
                                                    Spaces and separators are ignored while matching.
                                                </p>
                                            </div>
                                        </div>

                                        <label
                                            className={`bulk-policy-dropzone ${bulkDocumentDragActive ? 'is-dragging' : ''} ${bulkDocumentFiles.length ? 'has-file' : ''}`}
                                            onDragOver={(event) => {
                                                event.preventDefault();
                                                setBulkDocumentDragActive(true);
                                            }}
                                            onDragLeave={() => setBulkDocumentDragActive(false)}
                                            onDrop={(event) => {
                                                event.preventDefault();
                                                setBulkDocumentDragActive(false);
                                                handleBulkDocumentFiles(event.dataTransfer.files);
                                            }}
                                        >
                                            <input
                                                type="file"
                                                multiple
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                onChange={(event) => handleBulkDocumentFiles(event.target.files)}
                                            />
                                            <UploadCloud size={34} />
                                            <strong>
                                                {bulkDocumentFiles.length
                                                    ? `${bulkDocumentFiles.length} document${bulkDocumentFiles.length === 1 ? '' : 's'} selected`
                                                    : 'Select or drop policy documents here'}
                                            </strong>
                                            <small>PDF, DOC, DOCX, JPG, JPEG, and PNG; maximum 20 MB each and 200 files</small>
                                        </label>

                                        {bulkDocumentUploading && (
                                            <div className="bulk-policy-processing">
                                                <span />
                                                <strong>Matching and uploading policy documents...</strong>
                                            </div>
                                        )}

                                        {bulkDocumentResult && (
                                            <>
                                                <div className="bulk-policy-result-grid">
                                                    <div>
                                                        <span>Total Files</span>
                                                        <strong>{bulkDocumentResult.totalFiles ?? 0}</strong>
                                                    </div>
                                                    <div className="success">
                                                        <span>Uploaded</span>
                                                        <strong>{bulkDocumentResult.successCount ?? 0}</strong>
                                                    </div>
                                                    <div className="failed">
                                                        <span>Failed</span>
                                                        <strong>{bulkDocumentResult.failedCount ?? 0}</strong>
                                                    </div>
                                                </div>

                                                {(bulkDocumentResult.failed || []).length > 0 && (
                                                    <div className="bulk-policy-preview bulk-policy-error-preview">
                                                        <div className="bulk-policy-preview-head">
                                                            <h4>Documents Not Uploaded</h4>
                                                        </div>
                                                        <div className="bulk-policy-preview-table-wrap">
                                                            <table>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Filename</th>
                                                                        <th>Policy Number</th>
                                                                        <th>Reason</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {bulkDocumentResult.failed.map((item, index) => (
                                                                        <tr key={`${item.fileName}-${index}`}>
                                                                            <td>{item.fileName}</td>
                                                                            <td>{item.policyNumber || '-'}</td>
                                                                            <td>{item.reason}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {bulkDocumentError && <div className="bulk-policy-error">{bulkDocumentError}</div>}
                                    </div>

                                    <div className="bulk-policy-modal-actions">
                                        <button type="button" className="bulk-policy-cancel" onClick={closeBulkDocumentModal} disabled={bulkDocumentUploading}>
                                            {bulkDocumentResult ? 'Close' : 'Cancel'}
                                        </button>
                                        <button
                                            type="button"
                                            className="bulk-policy-confirm"
                                            onClick={uploadBulkPolicyDocuments}
                                            disabled={!bulkDocumentFiles.length || bulkDocumentUploading}
                                        >
                                            {bulkDocumentUploading ? 'Uploading...' : bulkDocumentResult ? 'Upload Again' : 'Upload Documents'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default RetailAddPolicy;
